import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sewaGedungService } from '../../services/sewaGedungService';
import { getAssetUrl } from '../../services/api';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { id as localeId } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import {
    FaBuilding, FaUser, FaIdCard, FaPhone, FaEnvelope, FaHome,
    FaUpload, FaFilePdf, FaCheckCircle, FaArrowLeft, FaArrowRight,
    FaInfoCircle, FaCalendarCheck, FaClipboardList, FaPrint,
    FaMapMarkedAlt, FaClock, FaFileAlt, FaWifi, FaCar, FaSnowflake,
    FaDesktop, FaChair, FaUsers, FaMusic, FaVideo, FaUtensils,
    FaParking, FaSearch, FaCalendarAlt, FaMoneyBillWave, FaBus,
    FaTruck, FaMotorcycle, FaTools, FaCogs, FaHardHat, FaTractor,
    FaToolbox, FaLaptop, FaCamera, FaMicrophone, FaPrinter,
    FaProjectDiagram, FaChalkboardTeacher, FaSpinner,
    FaChevronLeft as FaChevronLeftIcon,
    FaChevronRight as FaChevronRightIcon,
    FaExclamationTriangle, FaMapMarkerAlt, FaImage, FaPaperPlane
} from 'react-icons/fa';

const SewaGedung = () => {
    const [activeTab, setActiveTab] = useState('daftar');
    const [activeCategory, setActiveCategory] = useState('gedung');
    const [step, setStep] = useState(1);
    const [showForm, setShowForm] = useState(false);

    // Gedung dari API
    const [gedungList, setGedungList] = useState([]);
    const [loadingGedung, setLoadingGedung] = useState(true);
    const [gedungError, setGedungError] = useState('');

    // Slider state per gedung
    const [sliderIndex, setSliderIndex] = useState({});

    // ✅ Booked dates state
    const [bookedDates, setBookedDates] = useState([]);
    const [loadingBooked, setLoadingBooked] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Form state
    const [formData, setFormData] = useState({
        namaLengkap: '',
        nik: '',
        noTelp: '',
        email: '',
        alamat: '',
        pekerjaan: '',
        jenisInstansi: 'perseorangan',
        namaInstansi: '',
        gedungDipilih: '',
        jenisKegiatan: '',
        tanggalMulai: '',
        tanggalSelesai: '',
        jamMulai: '',
        jamSelesai: '',
        jumlahPeserta: '',
        deskripsiKegiatan: '',
        fasilitasTambahan: [],
        kebutuhanKhusus: '',
        ktp: null,
        suratPernyataan: null,
        dokumenPendukung: null,
        website_url: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [nomorRegistrasi, setNomorRegistrasi] = useState('');

    // ============ LOAD GEDUNG ============
    useEffect(() => {
        loadGedungs();
    }, []);

    const loadGedungs = async () => {
        setLoadingGedung(true);
        setGedungError('');
        try {
            const result = await sewaGedungService.getPublic();
            if (result.success) {
                setGedungList(result.data || []);
            } else {
                setGedungError(result.message || 'Gagal memuat data gedung');
            }
        } catch {
            setGedungError('Terjadi kesalahan saat memuat data');
        }
        setLoadingGedung(false);
    };

    // ============ LOAD BOOKED DATES ============
    const loadBookedDates = async (gedungId, month) => {
        if (!gedungId) return;
        setLoadingBooked(true);
        const bulan = month.getMonth() + 1;
        const tahun = month.getFullYear();

        const result = await sewaGedungService.getBookedDates(gedungId, bulan, tahun);
        if (result.success) {
            setBookedDates(result.data || []);
        } else {
            setBookedDates([]);
        }
        setLoadingBooked(false);
    };

    useEffect(() => {
        if (formData.gedungDipilih && step === 2) {
            loadBookedDates(formData.gedungDipilih, currentMonth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.gedungDipilih, currentMonth, step]);

    // ============ HELPERS ============
    const getFotoUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    // Cek apakah tanggal sudah dibooking
    const isDateBooked = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return bookedDates.includes(dateStr);
    };

    // Disabled: tanggal dibooking atau lampau
    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) return true;
        return isDateBooked(date);
    };

    // ============ SLIDER HANDLERS ============
    const handlePrevFoto = (gedungId, total) => {
        setSliderIndex(prev => ({
            ...prev,
            [gedungId]: ((prev[gedungId] ?? 0) - 1 + total) % total,
        }));
    };

    const handleNextFoto = (gedungId, total) => {
        setSliderIndex(prev => ({
            ...prev,
            [gedungId]: ((prev[gedungId] ?? 0) + 1) % total,
        }));
    };

    const handleGoToFoto = (gedungId, index) => {
        setSliderIndex(prev => ({ ...prev, [gedungId]: index }));
    };

    // ============ TABS ============
    const tabs = [
        { id: 'daftar', label: 'Daftar Aset', icon: FaBuilding },
        { id: 'formulir', label: 'Formulir Sewa', icon: FaFileAlt },
        { id: 'status', label: 'Cek Status', icon: FaSearch },
    ];

    const categories = [
        { id: 'gedung', label: 'Sewa Mess', icon: FaBuilding },
    ];

    const fasilitasTambahanOptions = [
        { value: 'sound_premium', label: 'Sound System Premium', biaya: 500000, icon: FaMusic },
        { value: 'lighting', label: 'Lighting Panggung', biaya: 750000, icon: FaVideo },
        { value: 'catering', label: 'Catering/Prasmanan', biaya: 50000, icon: FaUtensils, perOrang: true },
        { value: 'valet', label: 'Valet Parking', biaya: 300000, icon: FaParking },
        { value: 'dekorasi', label: 'Dekorasi Ruangan', biaya: 1000000, icon: FaDesktop },
        { value: 'fotografer', label: 'Fotografer/Videografer', biaya: 1500000, icon: FaVideo },
        { value: 'security', label: 'Petugas Keamanan', biaya: 400000, icon: FaUser },
        { value: 'cleaning', label: 'Cleaning Service', biaya: 300000, icon: FaCheckCircle },
    ];

    // ============ VALIDATION ============
    const validateStep = (stepNumber) => {
        const newErrors = {};

        if (stepNumber === 1) {
            if (!formData.namaLengkap) newErrors.namaLengkap = 'Nama lengkap wajib diisi';
            if (!formData.nik) newErrors.nik = 'NIK wajib diisi';
            else if (!/^[0-9]{16}$/.test(formData.nik)) newErrors.nik = 'NIK harus 16 digit angka';
            if (!formData.noTelp) newErrors.noTelp = 'Nomor telepon wajib diisi';
            else if (formData.noTelp.length < 10) newErrors.noTelp = 'Nomor telepon minimal 10 digit';
            if (!formData.alamat) newErrors.alamat = 'Alamat wajib diisi';
            else if (formData.alamat.length < 10) newErrors.alamat = 'Alamat minimal 10 karakter';
            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Format email tidak valid';
            }
        }

        if (stepNumber === 2) {
            if (!formData.gedungDipilih) newErrors.gedungDipilih = 'Gedung wajib dipilih';
            if (!formData.jenisKegiatan) newErrors.jenisKegiatan = 'Jenis kegiatan wajib diisi';
            if (!formData.tanggalMulai) newErrors.tanggalMulai = 'Tanggal mulai wajib diisi';
            if (!formData.tanggalSelesai) newErrors.tanggalSelesai = 'Tanggal selesai wajib diisi';
            if (!formData.jamMulai) newErrors.jamMulai = 'Jam mulai wajib diisi';
            if (!formData.jamSelesai) newErrors.jamSelesai = 'Jam selesai wajib diisi';
            if (!formData.jumlahPeserta) newErrors.jumlahPeserta = 'Jumlah peserta wajib diisi';
            if (!formData.deskripsiKegiatan) newErrors.deskripsiKegiatan = 'Deskripsi kegiatan wajib diisi';

            // ✅ Cek bentrok tanggal
            if (formData.tanggalMulai && formData.tanggalSelesai && bookedDates.length > 0) {
                const start = new Date(formData.tanggalMulai);
                const end = new Date(formData.tanggalSelesai);
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    const dateStr = format(d, 'yyyy-MM-dd');
                    if (bookedDates.includes(dateStr)) {
                        newErrors.tanggalMulai = 'Beberapa tanggal dalam rentang sudah dibooking';
                        newErrors.tanggalSelesai = 'Silakan pilih tanggal lain';
                        break;
                    }
                }
            }
        }

        if (stepNumber === 3) {
            if (!formData.ktp) newErrors.ktp = 'Foto/Scan KTP wajib diupload';
            if (!formData.suratPernyataan) newErrors.suratPernyataan = 'Surat pernyataan wajib diupload';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ============ FORM HANDLERS ============
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            fasilitasTambahan: checked
                ? [...prev.fasilitasTambahan, value]
                : prev.fasilitasTambahan.filter(f => f !== value),
        }));
    };

    const handleFileUpload = (e) => {
        const { name, files } = e.target;
        if (files[0]) {
            if (files[0].size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, [name]: 'File maksimal 5MB' }));
                return;
            }
            setFormData(prev => ({ ...prev, [name]: files[0] }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    // ============ SUBMIT ============
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(3)) return;

        setIsSubmitting(true);

        try {
            const fd = new FormData();
            fd.append('nama_lengkap', formData.namaLengkap.trim());
            fd.append('nik', formData.nik.trim());
            fd.append('no_telp', formData.noTelp.trim());
            fd.append('email', formData.email.trim());
            fd.append('alamat', formData.alamat.trim());
            fd.append('pekerjaan', formData.pekerjaan.trim());
            fd.append('jenis_instansi', formData.jenisInstansi);
            fd.append('nama_instansi', formData.namaInstansi.trim());

            fd.append('gedung_id', formData.gedungDipilih);
            fd.append('jenis_kegiatan', formData.jenisKegiatan.trim());
            fd.append('tanggal_mulai', formData.tanggalMulai);
            fd.append('tanggal_selesai', formData.tanggalSelesai);
            fd.append('jam_mulai', formData.jamMulai);
            fd.append('jam_selesai', formData.jamSelesai);
            fd.append('jumlah_peserta', formData.jumlahPeserta);
            fd.append('deskripsi_kegiatan', formData.deskripsiKegiatan.trim());

            formData.fasilitasTambahan.forEach((f, i) => {
                fd.append(`fasilitas_tambahan[${i}]`, f);
            });
            if (formData.kebutuhanKhusus) {
                fd.append('kebutuhan_khusus', formData.kebutuhanKhusus.trim());
            }

            if (formData.ktp) fd.append('file_ktp', formData.ktp);
            if (formData.suratPernyataan) fd.append('file_surat_pernyataan', formData.suratPernyataan);
            if (formData.dokumenPendukung) fd.append('file_dokumen_pendukung', formData.dokumenPendukung);

            fd.append('website_url', formData.website_url);

            const result = await sewaGedungService.submit(fd);

            if (result.success) {
                setNomorRegistrasi(result.data.nomor_registrasi);
                setShowSuccess(true);
                window.scrollTo(0, 0);
            } else {
                if (result.errors) {
                    const mapped = {};
                    Object.entries(result.errors).forEach(([k, v]) => {
                        mapped[k] = Array.isArray(v) ? v[0] : v;
                    });
                    setErrors(mapped);
                }
                alert(result.message || 'Gagal mengirim permohonan');
            }
        } catch {
            alert('Terjadi kesalahan. Silakan coba lagi.');
        }

        setIsSubmitting(false);
    };

    // ============ ESTIMASI BIAYA ============
    const hitungEstimasiBiaya = () => {
        if (!formData.gedungDipilih || !formData.tanggalMulai || !formData.tanggalSelesai) return null;
        const gedung = gedungList.find(g => String(g.id) === String(formData.gedungDipilih));
        if (!gedung) return null;

        const tglMulai = new Date(formData.tanggalMulai);
        const tglSelesai = new Date(formData.tanggalSelesai);
        const diffTime = Math.abs(tglSelesai - tglMulai);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        const biayaSewa = diffDays * Number(gedung.tarif_per_hari || 0);

        const biayaFasilitas = formData.fasilitasTambahan.reduce((total, fas) => {
            const fasilitas = fasilitasTambahanOptions.find(f => f.value === fas);
            if (!fasilitas) return total;
            if (fasilitas.perOrang && formData.jumlahPeserta) {
                return total + (fasilitas.biaya * parseInt(formData.jumlahPeserta));
            }
            return total + fasilitas.biaya;
        }, 0);

        const deposit = biayaSewa * 0.3;
        const biayaAdmin = 100000;

        return {
            nama: gedung.nama,
            diffDays,
            biayaSewa,
            biayaFasilitas,
            deposit,
            biayaAdmin,
            total: biayaSewa + biayaFasilitas + deposit + biayaAdmin,
        };
    };

    // ============ STEP INDICATOR ============
    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-8">
            {[
                { step: 1, label: 'Data Pemohon', icon: FaUser },
                { step: 2, label: 'Data Sewa', icon: FaCalendarAlt },
                { step: 3, label: 'Upload Dokumen', icon: FaUpload },
            ].map((item, index) => (
                <React.Fragment key={item.step}>
                    <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 ${step >= item.step ? 'bg-[#0D9488] text-white shadow-lg' : 'bg-gray-300 text-gray-600'
                            }`}>
                            {step > item.step ? <FaCheckCircle /> : <item.icon />}
                        </div>
                        <span className={`mt-2 text-xs font-medium hidden md:block ${step >= item.step ? 'text-[#0D9488]' : 'text-gray-500'
                            }`}>
                            {item.label}
                        </span>
                    </div>
                    {index < 2 && (
                        <div className={`w-16 md:w-24 h-1 mx-2 transition-all duration-300 ${step > item.step ? 'bg-[#0D9488]' : 'bg-gray-300'
                            }`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    // ============ SUCCESS PAGE ============
    if (showSuccess) {
        const estimasi = hitungEstimasiBiaya();
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-2xl mx-auto">
                    <div className="card-glass text-center">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                            <FaCheckCircle className="text-6xl text-green-500" />
                        </div>
                        <h2 className="text-3xl font-bold text-[#1E3A8A] mb-4">
                            Pengajuan Sewa Berhasil!
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Terima kasih telah mengajukan permohonan sewa.
                            Berikut adalah nomor registrasi Anda:
                        </p>

                        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white p-6 rounded-lg mb-6">
                            <p className="text-sm opacity-80 mb-2">Nomor Registrasi</p>
                            <p className="text-3xl font-bold font-mono">{nomorRegistrasi}</p>
                        </div>

                        {estimasi && (
                            <div className="bg-gray-50 p-6 rounded-lg mb-6 text-left">
                                <h3 className="font-bold text-[#1E3A8A] mb-3 flex items-center">
                                    <FaMoneyBillWave className="mr-2" />
                                    Estimasi Biaya Sewa
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Aset:</span>
                                        <span className="font-semibold">{estimasi.nama}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Durasi Sewa:</span>
                                        <span className="font-semibold">{estimasi.diffDays} hari</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Biaya Sewa:</span>
                                        <span>Rp {estimasi.biayaSewa.toLocaleString('id-ID')}</span>
                                    </div>
                                    {estimasi.biayaFasilitas > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Fasilitas Tambahan:</span>
                                            <span>Rp {estimasi.biayaFasilitas.toLocaleString('id-ID')}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Deposit:</span>
                                        <span>Rp {estimasi.deposit.toLocaleString('id-ID')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Biaya Admin:</span>
                                        <span>Rp {estimasi.biayaAdmin.toLocaleString('id-ID')}</span>
                                    </div>
                                    <hr />
                                    <div className="flex justify-between text-lg font-bold text-[#0D9488]">
                                        <span>Estimasi Total:</span>
                                        <span>Rp {estimasi.total.toLocaleString('id-ID')}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="text-left bg-blue-50 p-6 rounded-lg mb-6">
                            <h3 className="font-bold text-blue-800 mb-3 flex items-center">
                                <FaClipboardList className="mr-2" />
                                Informasi Selanjutnya:
                            </h3>
                            <ul className="space-y-3 text-sm text-blue-700">
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Simpan nomor registrasi untuk mengecek status permohonan</span>
                                </li>
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Petugas akan menghubungi Anda dalam 1-2 hari kerja</span>
                                </li>
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Pembayaran dilakukan setelah permohonan disetujui</span>
                                </li>
                            </ul>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4">
                            <button
                                onClick={() => window.print()}
                                className="flex-1 bg-[#0F172A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors flex items-center justify-center"
                            >
                                <FaPrint className="mr-2" /> Cetak Bukti
                            </button>
                            <button
                                onClick={() => {
                                    setShowSuccess(false);
                                    setStep(1);
                                    setShowForm(false);
                                    setBookedDates([]);
                                    setFormData({
                                        namaLengkap: '', nik: '', noTelp: '', email: '', alamat: '',
                                        pekerjaan: '', jenisInstansi: 'perseorangan', namaInstansi: '',
                                        gedungDipilih: '', jenisKegiatan: '', tanggalMulai: '', tanggalSelesai: '',
                                        jamMulai: '', jamSelesai: '', jumlahPeserta: '', deskripsiKegiatan: '',
                                        fasilitasTambahan: [], kebutuhanKhusus: '',
                                        ktp: null, suratPernyataan: null, dokumenPendukung: null,
                                        website_url: '',
                                    });
                                }}
                                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center"
                            >
                                <FaArrowLeft className="mr-2" /> Ajukan Sewa Baru
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-2">
                    <FaBuilding className="text-4xl text-[#0D9488]" />
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1E3A8A]">
                        Penyewaan Gedung Mess
                    </h1>
                </div>
                <p className="text-gray-600 text-lg ml-12">
                    Layanan sewa gedung aset Pemerintah Kota Magelang
                </p>
            </div>

            {/* Category */}
            <div className="flex flex-wrap gap-4 mb-8">
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => {
                            setActiveCategory(cat.id);
                            setShowForm(false);
                            setStep(1);
                        }}
                        className={`flex items-center px-6 py-4 rounded-xl font-semibold transition-all duration-300 ${activeCategory === cat.id
                                ? 'bg-[#1E3A8A] text-white shadow-lg transform scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                            }`}
                    >
                        <cat.icon className="mr-2 text-xl" />
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id);
                            if (tab.id !== 'formulir') setShowForm(false);
                            setStep(1);
                        }}
                        className={`flex items-center px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === tab.id
                                ? 'border-[#0D9488] text-[#0D9488] bg-[#0D9488]/5'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <tab.icon className="mr-2" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ============ DAFTAR ASET ============ */}
            {activeTab === 'daftar' && (
                <div>
                    {loadingGedung ? (
                        <div className="text-center py-20">
                            <FaSpinner className="animate-spin text-4xl text-[#0D9488] mx-auto mb-3" />
                            <p className="text-gray-500">Memuat data gedung...</p>
                        </div>
                    ) : gedungError ? (
                        <div className="text-center py-20">
                            <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-3" />
                            <p className="text-red-500">{gedungError}</p>
                        </div>
                    ) : gedungList.length === 0 ? (
                        <div className="text-center py-20">
                            <FaBuilding className="text-6xl text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Belum ada gedung tersedia</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {gedungList.map((item) => {
                                const fotos = item.foto_urls || [];
                                const currentIdx = sliderIndex[item.id] ?? 0;
                                const currentFoto = fotos[currentIdx];
                                const hasMultiple = fotos.length > 1;

                                const rawFotos = Array.isArray(item.fotos) ? item.fotos : [];
                                const currentRaw = rawFotos[currentIdx];
                                const label = (currentRaw && typeof currentRaw === 'object') ? currentRaw.label : '';

                                return (
                                    <div key={item.id} className={`card-glass overflow-hidden ${!item.tersedia ? 'opacity-60' : ''}`}>
                                        {/* SLIDER FOTO */}
                                        <div className="relative h-56 bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] flex items-center justify-center text-white overflow-hidden group">
                                            {currentFoto ? (
                                                <img
                                                    src={getFotoUrl(currentFoto)}
                                                    alt={item.nama}
                                                    className="w-full h-full object-cover transition-opacity duration-300"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            ) : null}

                                            {/* Label overlay */}
                                            {label && (
                                                <div className="absolute top-8 left-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg max-w-[70%] truncate z-20">
                                                    {label}
                                                </div>
                                            )}

                                            {/* Fallback emoji */}
                                            {!currentFoto && (
                                                <span className="text-6xl absolute">
                                                    {item.foto_emoji || '🏢'}
                                                </span>
                                            )}

                                            {/* Navigasi slider */}
                                            {hasMultiple && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePrevFoto(item.id, fotos.length)}
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                    >
                                                        <FaChevronLeftIcon size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleNextFoto(item.id, fotos.length)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                    >
                                                        <FaChevronRightIcon size={12} />
                                                    </button>

                                                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full z-10">
                                                        {currentIdx + 1} / {fotos.length}
                                                    </div>

                                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                                                        {fotos.map((_, i) => (
                                                            <button
                                                                key={i}
                                                                type="button"
                                                                onClick={() => handleGoToFoto(item.id, i)}
                                                                className={`rounded-full transition-all ${currentIdx === i
                                                                        ? 'bg-white w-4 h-1.5'
                                                                        : 'bg-white/50 hover:bg-white/80 w-1.5 h-1.5'
                                                                    }`}
                                                            />
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            {!item.tersedia && (
                                                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full z-10">
                                                    Tidak Tersedia
                                                </div>
                                            )}

                                            {fotos.length > 1 && (
                                                <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 z-10">
                                                    <FaImage size={9} /> {fotos.length}
                                                </div>
                                            )}
                                        </div>

                                        {/* Thumbnail strip */}
                                        {fotos.length > 1 && (
                                            <div className="flex gap-1 px-3 pt-3 overflow-x-auto">
                                                {fotos.map((f, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => handleGoToFoto(item.id, i)}
                                                        className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${currentIdx === i
                                                                ? 'border-[#0D9488] scale-105'
                                                                : 'border-gray-200 opacity-60 hover:opacity-100'
                                                            }`}
                                                    >
                                                        <img
                                                            src={getFotoUrl(f)}
                                                            alt={`thumb-${i}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.src = 'https://via.placeholder.com/50?text=?';
                                                            }}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Konten */}
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-[#1E3A8A] mb-2">{item.nama}</h3>
                                            <p className="text-sm text-gray-500 mb-2 flex items-center">
                                                <FaMapMarkerAlt className="mr-1" size={10} /> {item.lokasi}
                                            </p>
                                            <p className="text-sm text-gray-600 mb-3">{item.deskripsi}</p>

                                            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                                <div className="bg-gray-50 p-2 rounded">
                                                    <p className="font-semibold">{item.kapasitas}</p>
                                                    <p className="text-xs text-gray-500">Kapasitas</p>
                                                </div>
                                                <div className="bg-gray-50 p-2 rounded">
                                                    <p className="font-semibold">{item.luas || '-'}</p>
                                                    <p className="text-xs text-gray-500">Luas</p>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Fasilitas:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {Array.isArray(item.fasilitas) && item.fasilitas.map((fas, idx) => (
                                                        <span key={idx} className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">
                                                            {fas}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="border-t pt-4">
                                                <div className="flex justify-between items-center mb-3">
                                                    <div>
                                                        <p className="text-lg font-bold text-[#0D9488]">
                                                            Rp {Number(item.tarif_per_hari).toLocaleString('id-ID')}
                                                        </p>
                                                        <p className="text-xs text-gray-500">per hari</p>
                                                    </div>
                                                    {item.tarif_per_jam && (
                                                        <div>
                                                            <p className="text-sm font-semibold text-[#1E3A8A]">
                                                                Rp {Number(item.tarif_per_jam).toLocaleString('id-ID')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">per jam</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        setActiveTab('formulir');
                                                        setShowForm(true);
                                                        setFormData(prev => ({ ...prev, gedungDipilih: item.id }));
                                                        window.scrollTo(0, 0);
                                                    }}
                                                    disabled={!item.tersedia}
                                                    className={`w-full py-2 rounded-lg font-semibold transition-colors ${item.tersedia
                                                            ? 'btn-primary'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        }`}
                                                >
                                                    {item.tersedia ? 'Sewa Sekarang' : 'Tidak Tersedia'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ============ FORMULIR SEWA ============ */}
            {activeTab === 'formulir' && (
                <div>
                    {!showForm ? (
                        <div className="text-center py-12">
                            <FaBuilding className="text-6xl text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-500 mb-2">Pilih Aset Terlebih Dahulu</h3>
                            <p className="text-gray-400 mb-6">Silakan pilih gedung yang ingin disewa dari daftar</p>
                            <button onClick={() => setActiveTab('daftar')} className="btn-primary">
                                Lihat Daftar Aset
                            </button>
                        </div>
                    ) : (
                        <div>
                            {renderStepIndicator()}

                            <div className="max-w-4xl mx-auto">
                                <div className="card-glass">
                                    <form onSubmit={handleSubmit}>
                                        {/* Honeypot */}
                                        <input
                                            type="text"
                                            name="website_url"
                                            value={formData.website_url}
                                            onChange={handleInputChange}
                                            style={{ display: 'none', position: 'absolute', left: '-9999px' }}
                                            tabIndex="-1"
                                            autoComplete="off"
                                        />

                                        {/* ============ STEP 1 ============ */}
                                        {step === 1 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaUser className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">Data Pemohon</h2>
                                                        <p className="text-sm text-gray-500">Lengkapi data diri pemohon sewa</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Nama Lengkap <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="namaLengkap"
                                                            value={formData.namaLengkap}
                                                            onChange={handleInputChange}
                                                            maxLength={255}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.namaLengkap ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan nama lengkap"
                                                        />
                                                        {errors.namaLengkap && <p className="text-red-500 text-xs mt-1">{errors.namaLengkap}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            NIK <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="nik"
                                                            value={formData.nik}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                                                                setFormData(prev => ({ ...prev, nik: val }));
                                                                if (errors.nik) setErrors(prev => ({ ...prev, nik: '' }));
                                                            }}
                                                            maxLength={16}
                                                            inputMode="numeric"
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.nik ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan 16 digit NIK"
                                                        />
                                                        <p className="text-xs text-gray-400 mt-1">{formData.nik.length}/16 digit</p>
                                                        {errors.nik && <p className="text-red-500 text-xs mt-1">{errors.nik}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Nomor Telepon <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            name="noTelp"
                                                            value={formData.noTelp}
                                                            onChange={handleInputChange}
                                                            maxLength={20}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.noTelp ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="08123456789"
                                                        />
                                                        {errors.noTelp && <p className="text-red-500 text-xs mt-1">{errors.noTelp}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            maxLength={255}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="contoh@email.com"
                                                        />
                                                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Alamat Lengkap <span className="text-red-500">*</span>
                                                        </label>
                                                        <textarea
                                                            name="alamat"
                                                            value={formData.alamat}
                                                            onChange={handleInputChange}
                                                            rows="3"
                                                            maxLength={1000}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.alamat ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan alamat lengkap sesuai KTP"
                                                        />
                                                        {errors.alamat && <p className="text-red-500 text-xs mt-1">{errors.alamat}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Pekerjaan</label>
                                                        <input
                                                            type="text"
                                                            name="pekerjaan"
                                                            value={formData.pekerjaan}
                                                            onChange={handleInputChange}
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                                            placeholder="Masukkan pekerjaan"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Instansi</label>
                                                        <select
                                                            name="jenisInstansi"
                                                            value={formData.jenisInstansi}
                                                            onChange={handleInputChange}
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                                        >
                                                            <option value="perseorangan">Perseorangan</option>
                                                            <option value="perusahaan">Perusahaan</option>
                                                            <option value="instansi">Instansi Pemerintah</option>
                                                            <option value="organisasi">Organisasi/Komunitas</option>
                                                        </select>
                                                    </div>

                                                    {formData.jenisInstansi !== 'perseorangan' && (
                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Nama Instansi</label>
                                                            <input
                                                                type="text"
                                                                name="namaInstansi"
                                                                value={formData.namaInstansi}
                                                                onChange={handleInputChange}
                                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                                                placeholder="Masukkan nama instansi"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* ============ STEP 2 ============ */}
                                        {step === 2 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaCalendarAlt className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">Data Penyewaan</h2>
                                                        <p className="text-sm text-gray-500">Informasi gedung dan jadwal sewa</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Gedung <span className="text-red-500">*</span>
                                                        </label>
                                                        <select
                                                            name="gedungDipilih"
                                                            value={formData.gedungDipilih}
                                                            onChange={(e) => {
                                                                handleInputChange(e);
                                                                // Reset tanggal saat gedung ganti
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    gedungDipilih: e.target.value,
                                                                    tanggalMulai: '',
                                                                    tanggalSelesai: '',
                                                                }));
                                                            }}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.gedungDipilih ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        >
                                                            <option value="">Pilih Gedung</option>
                                                            {gedungList.filter(g => g.tersedia).map(g => (
                                                                <option key={g.id} value={g.id}>
                                                                    {g.nama} - {g.lokasi}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors.gedungDipilih && <p className="text-red-500 text-xs mt-1">{errors.gedungDipilih}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jenis Kegiatan <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="jenisKegiatan"
                                                            value={formData.jenisKegiatan}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.jenisKegiatan ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Seminar, Pernikahan, Rapat"
                                                        />
                                                        {errors.jenisKegiatan && <p className="text-red-500 text-xs mt-1">{errors.jenisKegiatan}</p>}
                                                    </div>

                                                    {/* ✅ KALENDER BOOKING */}
                                                    {formData.gedungDipilih && (
                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Pilih Tanggal Sewa <span className="text-red-500">*</span>
                                                            </label>

                                                            {/* Legend */}
                                                            <div className="mb-3 flex flex-wrap items-center gap-4 text-xs">
                                                                <div className="flex items-center gap-1">
                                                                    <span className="w-3 h-3 rounded-full bg-white border border-gray-300"></span>
                                                                    <span className="text-gray-600">Tersedia</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                                                                    <span className="text-gray-600">Sudah dibooking</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="w-3 h-3 rounded-full bg-gray-200"></span>
                                                                    <span className="text-gray-600">Tidak tersedia</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="w-3 h-3 rounded-full bg-[#0D9488]"></span>
                                                                    <span className="text-gray-600">Tanggal dipilih</span>
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                                {/* Kalender */}
                                                                <div className="bg-white rounded-lg border p-4 relative">
                                                                    {loadingBooked && (
                                                                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-lg">
                                                                            <FaSpinner className="animate-spin text-2xl text-[#0D9488]" />
                                                                        </div>
                                                                    )}

                                                                    <DayPicker
                                                                        mode="range"
                                                                        selected={{
                                                                            from: formData.tanggalMulai ? parseISO(formData.tanggalMulai) : undefined,
                                                                            to: formData.tanggalSelesai ? parseISO(formData.tanggalSelesai) : undefined,
                                                                        }}
                                                                        onSelect={(range) => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                tanggalMulai: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                                                                                tanggalSelesai: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                                                                            }));
                                                                            if (errors.tanggalMulai) setErrors(prev => ({ ...prev, tanggalMulai: '' }));
                                                                            if (errors.tanggalSelesai) setErrors(prev => ({ ...prev, tanggalSelesai: '' }));
                                                                        }}
                                                                        month={currentMonth}
                                                                        onMonthChange={setCurrentMonth}
                                                                        disabled={isDateDisabled}
                                                                        modifiers={{
                                                                            booked: (date) => isDateBooked(date),
                                                                        }}
                                                                        modifiersClassNames={{
                                                                            booked: 'bg-orange-400 text-white font-semibold line-through',
                                                                        }}
                                                                        locale={localeId}
                                                                        numberOfMonths={1}
                                                                        fromDate={new Date()}
                                                                        className="mx-auto"
                                                                    />
                                                                </div>

                                                                {/* Summary */}
                                                                <div className="space-y-3">
                                                                    <div className="p-4 bg-gray-50 rounded-lg">
                                                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                            Tanggal Mulai
                                                                        </label>
                                                                        <input
                                                                            type="date"
                                                                            value={formData.tanggalMulai}
                                                                            readOnly
                                                                            className={`w-full px-4 py-2.5 border rounded-lg bg-white ${errors.tanggalMulai ? 'border-red-500' : 'border-gray-300'
                                                                                }`}
                                                                        />
                                                                        {errors.tanggalMulai && (
                                                                            <p className="text-red-500 text-xs mt-1">{errors.tanggalMulai}</p>
                                                                        )}
                                                                    </div>

                                                                    <div className="p-4 bg-gray-50 rounded-lg">
                                                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                            Tanggal Selesai
                                                                        </label>
                                                                        <input
                                                                            type="date"
                                                                            value={formData.tanggalSelesai}
                                                                            readOnly
                                                                            className={`w-full px-4 py-2.5 border rounded-lg bg-white ${errors.tanggalSelesai ? 'border-red-500' : 'border-gray-300'
                                                                                }`}
                                                                        />
                                                                        {errors.tanggalSelesai && (
                                                                            <p className="text-red-500 text-xs mt-1">{errors.tanggalSelesai}</p>
                                                                        )}
                                                                    </div>

                                                                    {formData.tanggalMulai && formData.tanggalSelesai && (
                                                                        <div className="p-3 bg-[#0D9488]/10 border border-[#0D9488]/30 rounded-lg">
                                                                            <p className="text-xs text-[#0D9488] font-semibold">
                                                                                ✓ {(() => {
                                                                                    const t1 = new Date(formData.tanggalMulai);
                                                                                    const t2 = new Date(formData.tanggalSelesai);
                                                                                    const diff = Math.ceil(Math.abs(t2 - t1) / (1000 * 60 * 60 * 24)) + 1;
                                                                                    return `${diff} hari sewa dipilih`;
                                                                                })()}
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                    {bookedDates.length > 0 && (
                                                                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                                            <p className="text-xs text-orange-700 font-semibold mb-1">
                                                                                ⚠️ {bookedDates.length} tanggal sudah dibooking bulan ini
                                                                            </p>
                                                                            <p className="text-[10px] text-orange-600">
                                                                                Tanggal berwarna <span className="font-bold">oranye</span> tidak bisa dipilih
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jam Mulai <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            name="jamMulai"
                                                            value={formData.jamMulai}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.jamMulai ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        />
                                                        {errors.jamMulai && <p className="text-red-500 text-xs mt-1">{errors.jamMulai}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jam Selesai <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            name="jamSelesai"
                                                            value={formData.jamSelesai}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.jamSelesai ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        />
                                                        {errors.jamSelesai && <p className="text-red-500 text-xs mt-1">{errors.jamSelesai}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jumlah Peserta <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="jumlahPeserta"
                                                            value={formData.jumlahPeserta}
                                                            onChange={handleInputChange}
                                                            min="1"
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.jumlahPeserta ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Estimasi jumlah peserta"
                                                        />
                                                        {errors.jumlahPeserta && <p className="text-red-500 text-xs mt-1">{errors.jumlahPeserta}</p>}
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Deskripsi Kegiatan <span className="text-red-500">*</span>
                                                        </label>
                                                        <textarea
                                                            name="deskripsiKegiatan"
                                                            value={formData.deskripsiKegiatan}
                                                            onChange={handleInputChange}
                                                            rows="4"
                                                            maxLength={2000}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] ${errors.deskripsiKegiatan ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Jelaskan detail kegiatan"
                                                        />
                                                        {errors.deskripsiKegiatan && <p className="text-red-500 text-xs mt-1">{errors.deskripsiKegiatan}</p>}
                                                    </div>

                                                    {/* Fasilitas Tambahan */}
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-3">Fasilitas Tambahan</label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            {fasilitasTambahanOptions.map(fasilitas => (
                                                                <label
                                                                    key={fasilitas.value}
                                                                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        value={fasilitas.value}
                                                                        checked={formData.fasilitasTambahan.includes(fasilitas.value)}
                                                                        onChange={handleCheckboxChange}
                                                                        className="mr-2 text-[#0D9488] focus:ring-[#0D9488]"
                                                                    />
                                                                    <fasilitas.icon className="text-gray-600 mr-2" />
                                                                    <div>
                                                                        <span className="text-sm font-medium text-gray-700">{fasilitas.label}</span>
                                                                        <p className="text-xs text-gray-500">
                                                                            +Rp {fasilitas.biaya.toLocaleString('id-ID')}
                                                                            {fasilitas.perOrang ? '/orang' : ''}
                                                                        </p>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Kebutuhan Khusus</label>
                                                        <textarea
                                                            name="kebutuhanKhusus"
                                                            value={formData.kebutuhanKhusus}
                                                            onChange={handleInputChange}
                                                            rows="2"
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                                            placeholder="Sebutkan kebutuhan khusus"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Estimasi Biaya */}
                                                {hitungEstimasiBiaya() && (
                                                    <div className="bg-green-50 p-4 rounded-lg">
                                                        <h4 className="font-semibold text-green-800 mb-2">Estimasi Biaya</h4>
                                                        <div className="space-y-1 text-sm">
                                                            {(() => {
                                                                const est = hitungEstimasiBiaya();
                                                                return (
                                                                    <>
                                                                        <div className="flex justify-between">
                                                                            <span>Sewa {est.diffDays} hari:</span>
                                                                            <span>Rp {est.biayaSewa.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        {est.biayaFasilitas > 0 && (
                                                                            <div className="flex justify-between">
                                                                                <span>Fasilitas Tambahan:</span>
                                                                                <span>Rp {est.biayaFasilitas.toLocaleString('id-ID')}</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex justify-between">
                                                                            <span>Deposit (30%):</span>
                                                                            <span>Rp {est.deposit.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span>Biaya Admin:</span>
                                                                            <span>Rp {est.biayaAdmin.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        <hr className="border-green-200" />
                                                                        <div className="flex justify-between font-bold">
                                                                            <span>Total Estimasi:</span>
                                                                            <span>Rp {est.total.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ============ STEP 3 ============ */}
                                        {step === 3 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaUpload className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">Upload Dokumen</h2>
                                                        <p className="text-sm text-gray-500">Lengkapi dokumen persyaratan sewa</p>
                                                    </div>
                                                </div>

                                                <div className="bg-yellow-50 p-4 rounded-lg flex items-start">
                                                    <FaInfoCircle className="text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-sm font-semibold text-yellow-800 mb-1">Ketentuan Upload:</p>
                                                        <ul className="text-sm text-yellow-700 list-disc list-inside">
                                                            <li>Format: PDF, JPG, PNG (Maks. 5MB/file)</li>
                                                            <li>Tanda <span className="text-red-500">*</span> wajib diupload</li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Foto/Scan KTP <span className="text-red-500">*</span>
                                                                </label>
                                                                {formData.ktp && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.ktp.name} ({(formData.ktp.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                                {errors.ktp && <p className="text-red-500 text-xs mt-1">{errors.ktp}</p>}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="ktp"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Surat Pernyataan <span className="text-red-500">*</span>
                                                                </label>
                                                                {formData.suratPernyataan && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.suratPernyataan.name} ({(formData.suratPernyataan.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                                {errors.suratPernyataan && <p className="text-red-500 text-xs mt-1">{errors.suratPernyataan}</p>}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="suratPernyataan"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Dokumen Pendukung Lainnya (Opsional)
                                                                </label>
                                                                {formData.dokumenPendukung && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.dokumenPendukung.name} ({(formData.dokumenPendukung.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="dokumenPendukung"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Navigation */}
                                        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                                            {step > 1 ? (
                                                <button
                                                    type="button"
                                                    onClick={prevStep}
                                                    className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                                >
                                                    <FaArrowLeft className="mr-2" /> Sebelumnya
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowForm(false)}
                                                    className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                                >
                                                    <FaArrowLeft className="mr-2" /> Kembali
                                                </button>
                                            )}

                                            {step < 3 ? (
                                                <button
                                                    type="button"
                                                    onClick={nextStep}
                                                    className="flex items-center btn-primary"
                                                >
                                                    Selanjutnya <FaArrowRight className="ml-2" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className={`flex items-center btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <FaSpinner className="animate-spin mr-2" />
                                                            Mengirim...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaPaperPlane className="mr-2" />
                                                            Kirim Permohonan Sewa
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ============ CEK STATUS ============ */}
            {activeTab === 'status' && (
                <div className="max-w-2xl mx-auto">
                    <div className="card-glass">
                        <h3 className="text-xl font-bold text-[#1E3A8A] mb-4 flex items-center">
                            <FaSearch className="mr-2 text-[#0D9488]" />
                            Cek Status Permohonan Sewa
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Nomor Registrasi</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    placeholder="SEWA-GDG-2026-00001"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">NIK</label>
                                <input
                                    type="text"
                                    maxLength={16}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488]"
                                    placeholder="Masukkan 16 digit NIK"
                                />
                            </div>
                            <button className="btn-primary w-full">
                                <FaSearch className="mr-2 inline" /> Cek Status
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SewaGedung;